"use client";

import React, { useEffect, useRef } from "react";
import style from "./Footer.module.scss";
import { FooterAnimation } from "./FooterAnimation/FooterAnimation";
import { FooterContact } from "./FooterContact/FooterContact";
import { gsap, ScrollTrigger } from "@/utils/gsap";
import { useGsapBurgerTimeline } from "@/utils/useGsapBurgerTimeline";
import { useDeviceDetector } from "@/utils/useDeviceDetector";
import { scheduleDebouncedRefresh } from "@/utils/refresh-debounce";

export const Footer = () => {
  const sectionRef = useRef(null);
  const footerOneRef = useRef(null);
  const footerTwoRef = useRef(null);
  const timelineRef = useRef(null);

  const { isMobile } = useDeviceDetector();

  useGsapBurgerTimeline({
    triggerClass: style.footerOne,
  });

  useEffect(() => {
    timelineRef.current?.scrollTrigger?.kill();
    timelineRef.current?.kill();

    if (isMobile) return;

    gsap.set(footerTwoRef.current, { y: "100%", autoAlpha: 1 });

    timelineRef.current = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top top",
        end: "+=100%",
        pin: true,
        pinSpacing: true,
        scrub: true,
        invalidateOnRefresh: true,
        preventOverlaps: true,
        fastScrollEnd: false,
        refreshPriority: -1,
      },
      ease: "power4",
    });
    timelineRef.current.fromTo(
      ".footerOne .scaleText",
      { scale: 1, y: 0 },
      { scale: 0.9, y: -75 }
    );
    timelineRef.current.fromTo(
      footerTwoRef.current,
      {
        y: "100%",
      },
      {
        y: "0%",
      },
      "<+=0.25"
    );

    return () => {
      timelineRef.current?.scrollTrigger?.kill();
      timelineRef.current?.kill();
    };
  }, [isMobile, sectionRef, footerOneRef, footerTwoRef]);

  useEffect(() => {
    if (!sectionRef.current) return undefined;

    const preRefreshTrigger = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: () => `top bottom+=${window.innerHeight}`,
      onEnter: () => scheduleDebouncedRefresh(),
      onEnterBack: () => scheduleDebouncedRefresh(),
    });

    return () => preRefreshTrigger.kill();
  }, []);

  return (
    <footer className={`${style.footerContainer}`} ref={sectionRef}>
      <div className={`${style.footerOne} footerOne`} ref={footerOneRef}>
        <FooterAnimation />
      </div>
      <div className={`${style.footerTwo} footerTwo`} ref={footerTwoRef}>
        <FooterContact />
      </div>
    </footer>
  );
};
